import type { Club } from '@/types';
import type { CompleteFieldSelector } from '@/types/fields-api.model';
import { SPORT_FIELDS } from '../sport/sport-fields.config';

const CLUB_FIELDS_MINIMAL = {
  id: true,
  name: true,
} as const;

/** Just enough to know which sports a club has courts for — labels come from the catalog. */
const CLUB_FIELDS_COURT_SPORTS = {
  ...CLUB_FIELDS_MINIMAL,
  courts: {
    sport: { uid: true },
  },
} as const;

/** What the weekly Turnos grid reads: schedule, timezone and the courts with their sport. */
const CLUB_FIELDS_TURNOS = {
  ...CLUB_FIELDS_MINIMAL,
  address: { timezone: true },
  availability: {
    dayOfWeek: true,
    timeRange: { startTime: true, endTime: true },
  },
  courts: {
    // The grid counts courts per sport. Without `id`, every court of a sport maps to an equal
    // CourtDTO and the backend's Set<CourtDTO> collapses them into one.
    id: true,
    sport: { uid: true, name: true },
  },
} as const;

const CLUB_FIELDS_BASIC = {
  ...CLUB_FIELDS_MINIMAL,
  status: true,
  email: true,
  address: {
    id: true,
    city: true,
    street: true,
    zipCode: true,
    latitude: true,
    longitude: true,
    state: true,
    country: {
      isoCode: true,
      name: true,
      phonePrefix: true,
      flag2: true,
    },
    timezone: true,
  },
  managers: {
    email: true,
    username: true,
    name: true,
  },
  instructors: {
    email: true,
    username: true,
    name: true,
  },
} as const;

const CLUB_FIELDS_DEFAULT = {
  ...CLUB_FIELDS_BASIC,
  courts: {
    id: true,
    clubId: true,
    name: true,
    surface: true,
    wallType: true,
    hasRoof: true,
    sport: SPORT_FIELDS.FULL,
    price: {
      value: true,
      id: true,
      currency: {
        isoCode: true,
        name: true,
      },
    },
  },
  bannerUrl: true,
} as const;

const CLUB_FIELDS_LIST = {
  ...CLUB_FIELDS_DEFAULT,
  availability: {
    date: true,
    dayOfWeek: true,
    timeRange: {
      courtsQuantity: true,
      qty: true,
      startTime: true,
      endTime: true,
    },
  },
} as const;

const CLUB_FIELDS_FULL: CompleteFieldSelector<Club> = {
  ...CLUB_FIELDS_LIST,
  cuit: true,
  instagramLink: true,
  facebookLink: true,
  cellphone: true,
  courts: {
    id: true,
    clubId: true,
    name: true,
    surface: true,
    wallType: true,
    hasRoof: true,
    sport: SPORT_FIELDS.FULL,
    price: {
      value: true,
      id: true,
      currency: {
        isoCode: true,
        name: true,
      },
    },
  },
  schedules: {
    date: true,
    dayOfWeek: true,
    timeRange: {
      courtsQuantity: true,
      qty: true,
      startTime: true,
      endTime: true,
    },
  },
};

export const CLUB_FIELDS = {
  MINIMAL: CLUB_FIELDS_MINIMAL,
  COURT_SPORTS: CLUB_FIELDS_COURT_SPORTS,
  TURNOS: CLUB_FIELDS_TURNOS,
  BASIC: CLUB_FIELDS_BASIC,
  DEFAULT: CLUB_FIELDS_DEFAULT,
  FULL: CLUB_FIELDS_FULL,
  LIST: CLUB_FIELDS_LIST,
} as const;
